import { combine, createEvent, createStore, sample } from 'effector'

import * as chat from '@/entities/chat'
import { persist } from '@/shared/lib/persist/local'
import { isNotNull } from '@/shared/lib/types'

import * as channelModel from './model.channel'
import * as connModel from './model.conn'

// --- Unread state ---

// channel id -> message id -> read state
export const $chatReplyRead = createStore<Record<string, Record<string, boolean>>>({})

// store read state in local storage to persist it between sessions
persist({
    store: $chatReplyRead,
    key: 'chat.reply-read',
    keyPrefix: chat.$userInfo.map((userInfo) => `${userInfo?.user_id}:`),
    pickup: chat.$userInfo.updates.filter({ fn: isNotNull }),
})

// channel id -> list of unread message ids
export const $chatReplyUnread = createStore<Record<string, string[]>>({})
export const $chatChannelReplyUnread = combine(
    channelModel.$chatChannel,
    $chatReplyUnread,
    (channel, unread) => unread[channel] ?? []
)

export const messageReplyRead = createEvent<{ messageId: string }>()

// mark message as read
sample({
    clock: messageReplyRead,
    source: { channel: channelModel.$chatChannel, read: $chatReplyRead },
    fn: ({ channel, read }, { messageId }) => readReply(read, channel, messageId),
    target: $chatReplyRead,
})

// remove message from unread list
sample({
    clock: messageReplyRead,
    source: {
        channel: channelModel.$chatChannel,
        unread: $chatReplyUnread,
    },
    fn: ({ channel, unread }, { messageId }) => removeUnreadMessage(unread, channel, messageId),
    target: $chatReplyUnread,
})

// add message to unread list
sample({
    clock: connModel.chatMessageReceived,
    source: {
        userInfo: chat.$userInfo,
        unread: $chatReplyUnread,
    },
    fn: ({ userInfo, unread }, { event, channel }) => {
        const message = event.payload
        const replyMessageId = message.reply_message_id
        const replyUserId = message.reply_user_id

        // no reply message
        if (!replyMessageId || !replyUserId) {
            return unread
        }

        // reply to another user
        if (userInfo?.user_id !== replyUserId) {
            return unread
        }

        return addUnreadMessage(unread, channel, message.id)
    },
    target: $chatReplyUnread,
})

// clean up old read messages when history received
sample({
    clock: channelModel.channelHistoryFx.done,
    source: $chatReplyRead,
    fn: (read, { params: { channel }, result: history }) => {
        const readIds = Object.keys(read[channel] ?? {})
        const historyIds = new Set(history.messages.map((item) => item.id))

        const newRead: Record<string, boolean> = {}
        for (const readId of readIds) {
            if (!historyIds.has(readId)) {
                continue
            }

            newRead[readId] = true
        }

        return {
            ...read,
            [channel]: newRead,
        }
    },
    target: $chatReplyRead,
})

// mark all replies to current user as unread, except those that are already read
sample({
    clock: channelModel.channelHistoryFx.done,
    source: {
        userInfo: chat.$userInfo,
        unread: $chatReplyUnread,
        read: $chatReplyRead,
    },
    fn: ({ userInfo, unread, read }, { params: { channel }, result: history }) => {
        if (!userInfo) {
            return unread
        }

        const unreadIds = history.messages
            // keep only replies to current user
            .filter((message) => message.reply_user_id === userInfo.user_id)
            // keep only unread messages
            .filter((message) => !read[channel]?.[message.id])
            .map((message) => message.id)

        return {
            ...unread,
            [channel]: unreadIds,
        }
    },
    target: $chatReplyUnread,
})

// sum unread messages across all channels
sample({
    clock: $chatReplyUnread,
    fn: (unread) => Object.values(unread).flat().length,
    target: chat.$chatUnreadReplyCounter,
})

function readReply(
    read: Record<string, Record<string, boolean>>,
    channel: string,
    messageId: string
): Record<string, Record<string, boolean>> {
    return {
        ...read,
        [channel]: {
            ...read[channel],
            [messageId]: true,
        },
    }
}

function addUnreadMessage(
    unread: Record<string, string[]>,
    channel: string,
    messageId: string
): Record<string, string[]> {
    return {
        ...unread,
        [channel]: [...(unread[channel] ?? []), messageId],
    }
}

function removeUnreadMessage(
    unread: Record<string, string[]>,
    channel: string,
    messageId: string
): Record<string, string[]> {
    return {
        ...unread,
        [channel]: unread[channel]?.filter((id) => id !== messageId) ?? [],
    }
}
